import { Component } from "@angular/core";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { CollectionSnapshotGameGroup, GameType, GetCollectionSnapshotResponse, ItemType } from "@apiserver-rest/ponder-data-client-base";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { take } from "rxjs";
import { CollectionSnapshotItemsChartComponent } from "./collection-snapshot-items-chart.component";
import { CollectionSnapshotValueChartComponent } from "./collection-snapshot-value-chart.component";
import { isMobileScreen } from "@ponder-shared/utils/screenBreakpoints";
import { UserStatsContainer } from "../user-stats-container.component";

@Component({
    selector: "user-collection-general-stats",
    imports: [
        LoadingComponent,
        CollectionSnapshotItemsChartComponent,
        CollectionSnapshotValueChartComponent
    ],
    templateUrl: "./user-collection-general-stats.component.html"
})
export class UserCollectionGeneralStatsComponent extends UserStatsContainer {
    public isLoading: boolean = true;
    public loadingSuccess: boolean = false;

    private gameFilter?: GameType = undefined;
    private itemTypeFilter?: ItemType = undefined;
    private snapshotGameGroups?: CollectionSnapshotGameGroup[] = undefined;
    public snapshotSummaries: SnapshotDailySummary[] = [];

    constructor(ponderDataClient: PonderDataClient) {
        super(ponderDataClient);
    }

    public override onInitialize(): void {
        this.ponderDataClient
            .collectionSnapshots(this.username, undefined, undefined)
            .pipe(take(1))
            .subscribe({
                next: (response: GetCollectionSnapshotResponse) => {
                    if (response.success) {
                        this.snapshotGameGroups = response.games;
                        this.loadingSuccess = true;
                    }

                    this.refineVisibleSnapshots();
                }
            })
            .add(() => { this.isLoading = false; })
    }

    private refineVisibleSnapshots(): void {
        this.snapshotSummaries = this.buildSnapshotSummaries();
    }

    private buildSnapshotSummaries(): SnapshotDailySummary[] {
        if (!this.snapshotGameGroups?.length) {
            return [];
        }

        var snapshotDates = new Map<Date, SnapshotDailySummary>();

        this.snapshotGameGroups.forEach(gameGroup => {
            if (!this.gameFilter || (this.gameFilter == gameGroup.game)) {
                gameGroup.itemGroups?.forEach(itemGroup => {
                    if (!this.itemTypeFilter || (this.itemTypeFilter == itemGroup.item)) {
                        itemGroup.snapshots?.forEach(snapshot => {
                            if (!snapshotDates.has(snapshot.snapshotDate)) {
                                snapshotDates.set(snapshot.snapshotDate, { date: snapshot.snapshotDate, items: 0, distinct: 0, value: 0 });
                            }

                            var summary = snapshotDates.get(snapshot.snapshotDate)!;

                            summary.items += snapshot.itemCount;
                            summary.distinct += snapshot.distinctCount;
                            summary.value += snapshot.value;
                        });
                    }
                });
            }
        });

        var snapshotSummaries = [... snapshotDates.values() ];
        snapshotSummaries.sort((a, b) => { return a.date.getTime() - b.date.getTime(); });      // Earliest to newest
        
        return snapshotSummaries;
    }

    public isMobileScreen(): boolean {
        return isMobileScreen();
    }
}

export type SnapshotDailySummary = {
    date: Date,
    items: number,
    distinct: number,
    value: number
};

export function getSnapshotSummaryFromDaysAgo(days: number, now: number, snapshotSummaries: SnapshotDailySummary[]): SnapshotDailySummary {
    // Get the first snapshot that is at least the give number of days in the past
    var delta = days * 24 * 60 * 60 * 1000;

    for (let i = (snapshotSummaries.length - 1) ; i >= 0; --i) {
        if ((now - snapshotSummaries[i].date.getTime()) >= delta) {
            return snapshotSummaries[i];
        }
    }

    return snapshotSummaries[0];
}